/**
 * GlobalDatePicker
 * A beautiful range picker with quick presets + interactive calendar,
 * all in the active display timezone. Sits in the TopNav and drives useDateStore.
 */
import type { DatePreset } from "@/store/useDateStore";
import {
  calcPresetRange,
  PRESET_LABELS,
  useDateStore,
} from "@/store/useDateStore";
import { cn } from "@/utils/cn";
import { todayDisplayDate, toDisplayDayjs } from "@/utils/displayTime";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

/* ────────────────────────────────────────────────────────────
   Calendar helpers
───────────────────────────────────────────────────────────── */

interface CalDay {
  dateStr: string; // YYYY-MM-DD
  label: number; // numeric day
  isCurrentMonth: boolean;
  isToday: boolean;
  isFuture: boolean;
}

/** Build a 42-cell (6 × 7) calendar grid starting Monday. */
function buildCalendarDays(year: number, month0: number): CalDay[] {
  const paddedM = String(month0 + 1).padStart(2, "0");
  const firstStr = `${year}-${paddedM}-01`;
  const first = toDisplayDayjs(firstStr);
  const todayStr = todayDisplayDate();

  // dayjs .day() → 0=Sun … convert to Mon-first: (dow+6)%7
  const firstDow = (first.day() + 6) % 7;
  const daysInMonth = first.daysInMonth();
  const days: CalDay[] = [];

  // Padding from previous month
  for (let i = firstDow - 1; i >= 0; i--) {
    const d = first.subtract(i + 1, "day");
    days.push({
      dateStr: d.format("YYYY-MM-DD"),
      label: d.date(),
      isCurrentMonth: false,
      isToday: false,
      isFuture: false,
    });
  }

  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${paddedM}-${String(d).padStart(2, "0")}`;
    days.push({
      dateStr,
      label: d,
      isCurrentMonth: true,
      isToday: dateStr === todayStr,
      isFuture: dateStr > todayStr,
    });
  }

  // Padding to next month to fill 42 cells
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    const d = first.date(daysInMonth).add(i, "day");
    const dateStr = d.format("YYYY-MM-DD");
    days.push({
      dateStr,
      label: d.date(),
      isCurrentMonth: false,
      isToday: false,
      isFuture: dateStr > todayStr,
    });
  }

  return days;
}

/** Compact range label, e.g. "Feb 14–20" or "Jan 22 – Feb 20, 2026". */
function fmtRange(start: string, end: string): string {
  if (!start || !end) return "—";
  const s = toDisplayDayjs(start);
  const e = toDisplayDayjs(end);
  if (start === end) return s.format("MMM D, YYYY");
  const sameYear = s.year() === e.year();
  const sameMonth = sameYear && s.month() === e.month();
  if (sameMonth) return `${s.format("MMM D")}–${e.format("D, YYYY")}`;
  if (sameYear) return `${s.format("MMM D")} – ${e.format("MMM D, YYYY")}`;
  return `${s.format("MMM D, YYYY")} – ${e.format("MMM D, YYYY")}`;
}

const DOW = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const PRESET_ORDER: DatePreset[] = [
  "today",
  "yesterday",
  "last7",
  "last30",
  "thisMonth",
  "lastMonth",
  "custom",
];

/* ────────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────────── */
export function GlobalDatePicker() {
  const { preset, startDate, endDate, setPreset, setCustomRange } =
    useDateStore();

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Calendar view state
  const [calYear, setCalYear] = useState(0);
  const [calMonth0, setCalMonth0] = useState(0);

  // Range-picking interaction
  const [selStart, setSelStart] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const picking = selStart !== null;

  // Initialise calendar position whenever we open
  useEffect(() => {
    if (open) {
      const d = toDisplayDayjs(endDate);
      setCalYear(d.year());
      setCalMonth0(d.month());
      setSelStart(null);
      setHovered(null);
    }
  }, [open, endDate]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSelStart(null);
        setHovered(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const days = buildCalendarDays(calYear, calMonth0);

  /* Effective display range:
       - While picking & hovering → preview tentative range
       - Otherwise → store range                          */
  const dispStart =
    picking && hovered ? [selStart!, hovered].sort()[0] : startDate;
  const dispEnd =
    picking && hovered
      ? [selStart!, hovered].sort()[1]
      : picking && selStart
        ? selStart
        : endDate;

  const dayIsStart = (d: string) => d === dispStart;
  const dayIsEnd = (d: string) => d === dispEnd;
  const dayInRange = (d: string) => d > dispStart && d < dispEnd;

  /* Month navigation */
  const todayDj = toDisplayDayjs();
  const isMaxMonth = calYear === todayDj.year() && calMonth0 >= todayDj.month();

  function prevMonth() {
    if (calMonth0 === 0) {
      setCalYear((y) => y - 1);
      setCalMonth0(11);
    } else {
      setCalMonth0((m) => m - 1);
    }
  }
  function nextMonth() {
    if (isMaxMonth) return;
    if (calMonth0 === 11) {
      setCalYear((y) => y + 1);
      setCalMonth0(0);
    } else {
      setCalMonth0((m) => m + 1);
    }
  }

  /* Day click */
  function handleDay(dateStr: string) {
    if (!picking) {
      // First click — anchor start of range
      setSelStart(dateStr);
    } else {
      // Second click — complete the range
      const [s, e] = [selStart!, dateStr].sort();
      setSelStart(null);
      setHovered(null);
      setCustomRange(s, e);
      setOpen(false);
    }
  }

  /* Preset click */
  function handlePreset(p: DatePreset) {
    if (p === "custom") {
      setPreset("custom");
      setSelStart(null);
      setHovered(null);
      return; // stay open so user can pick
    }
    setPreset(p);
    // Navigate calendar to the end of the selected range
    const range = calcPresetRange(p);
    const d = toDisplayDayjs(range.endDate);
    setCalYear(d.year());
    setCalMonth0(d.month());
    setOpen(false);
  }

  /* Trigger label */
  const calMonthLabel = toDisplayDayjs(
    `${calYear}-${String(calMonth0 + 1).padStart(2, "0")}-01`,
  ).format("MMMM YYYY");

  function getTriggerLabel(): string {
    if (picking) return "Pick end date…";
    switch (preset) {
      case "today":
        return `Today · ${toDisplayDayjs().format("MMM D")}`;
      case "yesterday":
        return `Yesterday · ${toDisplayDayjs().subtract(1, "day").format("MMM D")}`;
      case "custom":
        return fmtRange(startDate, endDate);
      default:
        return `${PRESET_LABELS[preset]} · ${fmtRange(startDate, endDate)}`;
    }
  }

  return (
    <div className="relative" ref={ref}>
      {/* ── Trigger button ── */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex h-8 items-center gap-2 rounded-lg border px-3 text-sm font-medium transition-all duration-150",
          open
            ? "border-brand-primary bg-brand-primary/8 text-brand-primary"
            : "border-border bg-surface-overlay text-text-secondary hover:border-border-focus hover:text-text-primary",
        )}
      >
        <Calendar className="h-3.5 w-3.5 shrink-0 text-current opacity-70" />
        <span className="max-w-[200px] truncate">{getTriggerLabel()}</span>
      </button>

      {/* ── Dropdown ── */}
      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-2 flex overflow-hidden rounded-xl border border-border bg-surface-raised"
          style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.14)" }}
        >
          {/* ── Left: Quick presets ── */}
          <div className="flex w-[138px] shrink-0 flex-col border-r border-border py-2">
            <p className="px-3 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-widest text-text-muted">
              Quick Select
            </p>
            {PRESET_ORDER.map((p) => (
              <button
                key={p}
                onClick={() => handlePreset(p)}
                className={cn(
                  "flex w-full items-center px-3 py-2 text-left text-[13px] transition-colors",
                  preset === p
                    ? "bg-brand-primary/10 font-semibold text-brand-primary"
                    : "text-text-secondary hover:bg-surface-hover hover:text-text-primary",
                )}
              >
                {PRESET_LABELS[p]}
              </button>
            ))}
          </div>

          {/* ── Right: Calendar ── */}
          <div className="p-4" style={{ width: 270 }}>
            {/* Month header */}
            <div className="mb-3 flex items-center justify-between gap-1">
              <button
                onClick={prevMonth}
                className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted hover:bg-surface-hover hover:text-text-primary"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="flex-1 text-center text-[13px] font-semibold text-text-primary">
                {calMonthLabel}
              </span>
              <button
                onClick={nextMonth}
                className={cn(
                  "flex h-7 w-7 items-center justify-center rounded-md text-text-muted",
                  isMaxMonth
                    ? "cursor-not-allowed opacity-25"
                    : "hover:bg-surface-hover hover:text-text-primary",
                )}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* Day-of-week headers */}
            <div className="mb-0.5 grid grid-cols-7">
              {DOW.map((d) => (
                <div
                  key={d}
                  className="flex h-8 items-center justify-center text-[11px] font-medium text-text-muted"
                >
                  {d}
                </div>
              ))}
            </div>

            {/* Day grid */}
            <div className="grid grid-cols-7">
              {days.map((day, i) => {
                const isS = dayIsStart(day.dateStr);
                const isE = dayIsEnd(day.dateStr);
                const inR = dayInRange(day.dateStr);
                const isSingle = dispStart === dispEnd;
                const disabled = day.isFuture;

                /* Range background — continuous strip between start & end.
                   Start cell: gradient right-half filled.
                   End cell:   gradient left-half filled.
                   In-range:   solid fill via Tailwind class.             */
                let stripStyle: React.CSSProperties | undefined;
                if (!isSingle) {
                  if (isS && !isE) {
                    stripStyle = {
                      background:
                        "linear-gradient(to right, transparent 50%, rgba(99,102,241,0.12) 50%)",
                    };
                  } else if (isE && !isS) {
                    stripStyle = {
                      background:
                        "linear-gradient(to left, transparent 50%, rgba(99,102,241,0.12) 50%)",
                    };
                  }
                }

                return (
                  <div
                    key={`${i}-${day.dateStr}`}
                    className={cn(
                      "relative flex h-8 items-center justify-center",
                      !isSingle && inR && "bg-brand-primary/10",
                    )}
                    style={stripStyle}
                    onMouseEnter={() =>
                      picking && !disabled && setHovered(day.dateStr)
                    }
                    onMouseLeave={() => picking && setHovered(null)}
                  >
                    <button
                      disabled={disabled}
                      onClick={() => !disabled && handleDay(day.dateStr)}
                      className={cn(
                        "relative z-10 flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium transition-colors",
                        // Future days
                        disabled && "cursor-not-allowed opacity-20",
                        // Faded external-month days
                        !day.isCurrentMonth &&
                          !disabled &&
                          "opacity-35 text-text-muted",
                        // Selected start / end
                        (isS || isE) && "bg-brand-primary text-white shadow-sm",
                        // In-range (not endpoint)
                        !isS && !isE && inR && "text-brand-primary",
                        // Today indicator (not selected)
                        day.isToday &&
                          !isS &&
                          !isE &&
                          "font-extrabold underline decoration-brand-primary decoration-2 underline-offset-2",
                        // Normal day hover
                        !disabled && !isS && !isE && "hover:bg-surface-hover",
                        // Normal day text
                        !disabled &&
                          day.isCurrentMonth &&
                          !isS &&
                          !isE &&
                          !inR &&
                          "text-text-primary",
                      )}
                    >
                      {day.label}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Footer: range summary / hint */}
            <div className="mt-3 border-t border-border-subtle pt-3">
              {picking ? (
                <p className="text-center text-[11px] text-brand-primary">
                  📅 Click a day to complete the range
                </p>
              ) : (
                <div className="flex items-center justify-between gap-2 text-xs">
                  <div className="flex-1 rounded-lg bg-surface-overlay px-2 py-1.5 text-center">
                    <p className="text-[10px] uppercase tracking-wide text-text-muted">
                      From
                    </p>
                    <p className="font-semibold text-text-primary">
                      {toDisplayDayjs(startDate).format("MMM D, YYYY")}
                    </p>
                  </div>
                  <ChevronRight className="h-3 w-3 shrink-0 text-text-muted" />
                  <div className="flex-1 rounded-lg bg-surface-overlay px-2 py-1.5 text-center">
                    <p className="text-[10px] uppercase tracking-wide text-text-muted">
                      To
                    </p>
                    <p className="font-semibold text-text-primary">
                      {toDisplayDayjs(endDate).format("MMM D, YYYY")}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
